import { useEffect, useState } from "react";
import { SUPPORTED_LANGS, type SupportedLang } from "@/lib/translations.functions";

const KEY = "sa.lang";
// Arabic and Russian are hidden for now (founder decision).
export const ACTIVE_LANGS = SUPPORTED_LANGS.filter((l) => l !== "ar" && l !== "ru");

function markTranslationPending(lang: SupportedLang) {
  if (typeof document === "undefined") return;
  if (lang === "en") document.documentElement.removeAttribute("data-sa-translation-pending");
  else document.documentElement.setAttribute("data-sa-translation-pending", "true");
}

export function detectBrowserLang(): SupportedLang {
  if (typeof navigator === "undefined") return "en";
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const raw of langs) {
    const short = (raw || "").slice(0, 2).toLowerCase();
    if ((ACTIVE_LANGS as readonly string[]).includes(short)) return short as SupportedLang;
  }
  return "en";
}

export function useLocale(): [SupportedLang, (l: SupportedLang) => void] {
  const [lang, setLang] = useState<SupportedLang>("en");

  useEffect(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("lang");
      if (fromUrl && (ACTIVE_LANGS as readonly string[]).includes(fromUrl)) {
        window.localStorage.setItem(KEY, fromUrl);
        setLang(fromUrl as SupportedLang);
        return;
      }
      const saved = window.localStorage.getItem(KEY) as SupportedLang | null;
      if (saved && (ACTIVE_LANGS as readonly string[]).includes(saved)) {
        setLang(saved);
      }
    } catch {}
  }, []);

  // Reflect the chosen locale on <html lang> so:
  //  1. Chrome/Safari/Edge auto-translate UI chrome when the user's browser
  //     language differs from the site language (free browser translation
  //     fallback for locales we don't natively support).
  //  2. Screen readers announce content in the correct language.
  useEffect(() => {
    if (typeof document !== "undefined") {
      try {
        const saved = window.localStorage.getItem(KEY);
        if (lang === "en" && saved && saved !== "en" && document.documentElement.lang !== "en") return;
      } catch {}
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    }
  }, [lang]);

  const update = (l: SupportedLang) => {
    // Hide the outgoing language before React can paint English source text.
    // AutoTranslate reveals the page after cached/reviewed text is applied.
    markTranslationPending(l);
    if (typeof document !== "undefined") {
      document.documentElement.lang = l;
      document.documentElement.dir = l === "ar" ? "rtl" : "ltr";
    }
    setLang(l);
    try {
      window.localStorage.setItem(KEY, l);
      const url = new URL(window.location.href);
      if (l === "en") url.searchParams.delete("lang");
      else url.searchParams.set("lang", l);
      window.history.replaceState(window.history.state, "", url);
      window.dispatchEvent(new CustomEvent("sa:lang-change", { detail: l }));
    } catch {}
  };

  useEffect(() => {
    const onChange = (e: Event) => {
      const l = (e as CustomEvent<SupportedLang>).detail;
      if (l && l !== lang) setLang(l);
    };
    window.addEventListener("sa:lang-change", onChange);
    return () => window.removeEventListener("sa:lang-change", onChange);
  }, [lang]);

  // TanStack navigation does not reload this hook, so resync the URL after
  // each route change. The saved preference is the durable source of truth.
  useEffect(() => {
    const syncUrl = () => {
      try {
        const url = new URL(window.location.href);
        if (lang === "en") url.searchParams.delete("lang");
        else url.searchParams.set("lang", lang);
        window.history.replaceState(window.history.state, "", url);
      } catch {}
    };
    window.addEventListener("popstate", syncUrl);
    window.addEventListener("hashchange", syncUrl);
    return () => {
      window.removeEventListener("popstate", syncUrl);
      window.removeEventListener("hashchange", syncUrl);
    };
  }, [lang]);

  return [lang, update];
}
